import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, X } from "lucide-react";
import { useLang } from "../i18n";
import { IMAGES } from "../data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const L = {
    en: {
        confirming: "Confirming your payment…",
        paidTitle: "Payment received.",
        paidBody: "Your attars are confirmed. Saad will reach out on WhatsApp to arrange delivery — your invisible crown awaits.",
        failTitle: "Payment not completed",
        failBody: "No charge was made. Your selection is still waiting whenever you're ready.",
        back: "Back to the Boutique",
        cancelTitle: "Payment cancelled",
        receipt: "Your Receipt", orderRef: "Order", paymentId: "Payment ID", total: "Total Paid", paidVia: "Paid via", delivery: "Delivery",
    },
    fr: {
        confirming: "Confirmation du paiement…",
        paidTitle: "Paiement reçu.",
        paidBody: "Vos attars sont confirmés. Saad vous contactera sur WhatsApp pour organiser la livraison — votre couronne invisible vous attend.",
        failTitle: "Paiement non abouti",
        failBody: "Aucun montant n'a été débité. Votre sélection vous attend quand vous serez prêt.",
        back: "Retour à la Boutique",
        cancelTitle: "Paiement annulé",
        receipt: "Votre Reçu", orderRef: "Commande", paymentId: "ID de paiement", total: "Total payé", paidVia: "Payé via", delivery: "Livraison",
    },
    ar: {
        confirming: "جارٍ تأكيد الدفع…",
        paidTitle: "تم استلام الدفع.",
        paidBody: "تم تأكيد طلبك. سيتواصل معك سعد عبر واتساب لترتيب التوصيل — تاجك الخفي بانتظارك.",
        failTitle: "لم يكتمل الدفع",
        failBody: "لم يتم خصم أي مبلغ. مختاراتك بانتظارك متى شئت.",
        back: "العودة إلى المتجر",
        cancelTitle: "أُلغي الدفع",
        receipt: "إيصالك", orderRef: "الطلب", paymentId: "رقم الدفع", total: "الإجمالي المدفوع", paidVia: "تم الدفع عبر", delivery: "التوصيل",
    },
    bn: {
        confirming: "আপনার পেমেন্ট নিশ্চিত করা হচ্ছে…",
        paidTitle: "পেমেন্ট গৃহীত হয়েছে।",
        paidBody: "আপনার আতর নিশ্চিত হয়েছে। সাদ হোয়াটসঅ্যাপে যোগাযোগ করে ডেলিভারির ব্যবস্থা করবেন — আপনার অদৃশ্য মুকুট অপেক্ষা করছে।",
        failTitle: "পেমেন্ট সম্পন্ন হয়নি",
        failBody: "কোনো টাকা কাটা হয়নি। আপনি প্রস্তুত হলেই আপনার নির্বাচন অপেক্ষা করছে।",
        back: "বুটিকে ফিরে যান",
        cancelTitle: "পেমেন্ট বাতিল হয়েছে",
        receipt: "আপনার রসিদ", orderRef: "অর্ডার", paymentId: "পেমেন্ট আইডি", total: "মোট পরিশোধিত", paidVia: "পেমেন্ট মাধ্যম", delivery: "ডেলিভারি",
    },
};

const Receipt = ({ r, l }) => (
    <div className="border border-gold/40 bg-cream/40 text-left max-w-md mx-auto mb-12 p-8" data-testid="order-receipt">
        <div className="flex justify-between items-baseline border-b border-gold/20 pb-4 mb-5">
            <span className="text-[10px] tracking-[0.3em] uppercase text-golddark">{l.receipt}</span>
            <span className="text-xs text-smoke" data-testid="receipt-order-ref">{l.orderRef} #{r.order_ref}</span>
        </div>
        <ul className="space-y-3 mb-5">
            {r.items.map((it) => (
                <li key={it.product_id} className="flex justify-between text-sm" data-testid={`receipt-item-${it.product_id}`}>
                    <span className="text-charcoal">{it.qty} × {it.name}</span>
                    <span className="font-serif text-charcoal">₹{it.price * it.qty}</span>
                </li>
            ))}
        </ul>
        {r.shipping_fee > 0 && (
            <div className="flex justify-between text-xs text-smoke border-t border-gold/15 pt-3 mb-1" data-testid="receipt-shipping">
                <span>{l.delivery}{r.shipping_courier ? ` · ${r.shipping_courier}` : ""}</span>
                <span>₹{r.shipping_fee}</span>
            </div>
        )}
        <div className="flex justify-between items-baseline border-t border-gold/20 pt-4">
            <span className="text-[10px] tracking-[0.25em] uppercase text-smoke">{l.total}</span>
            <span className="font-serif text-2xl text-charcoal" data-testid="receipt-total">₹{r.total}</span>
        </div>
        <p className="text-[10px] text-ash mt-5 tracking-wide break-all" data-testid="receipt-payment-id">
            {l.paymentId}: {r.payment_id} · {l.paidVia} {r.provider === "razorpay" ? "Razorpay" : "Stripe"}
        </p>
    </div>
);

const Shell = ({ children }) => (
    <div className="min-h-screen bg-ivory flex flex-col items-center justify-center px-6 text-center pt-24 pb-16" data-testid="payment-result-page">
        <img src={IMAGES.logo} alt="Al Zahra" className="w-20 mix-blend-multiply mb-10" />
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
            {children}
        </motion.div>
    </div>
);

export default function PaymentResult({ variant }) {
    const { lang } = useLang();
    const l = L[lang] ?? L.en;
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState(variant === "cancel" ? "failed" : "checking");
    const [receipt, setReceipt] = useState(null);

    useEffect(() => {
        if (variant !== "success" || !sessionId) return;
        let tries = 0;
        const poll = async () => {
            try {
                const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setReceipt(data.receipt ?? null);
                    setState("paid");
                    return;
                }
            } catch {}
            tries += 1;
            if (tries < 12) setTimeout(poll, 2500);
            else setState("failed");
        };
        poll();
    }, [variant, sessionId]);

    const isPaid = state === "paid";
    const isChecking = state === "checking";

    return (
        <Shell>
            {isChecking ? (
                <>
                    <div className="w-10 h-10 border border-gold border-t-transparent rounded-full animate-spin mx-auto mb-8" />
                    <p className="font-serif text-2xl text-smoke italic" data-testid="payment-confirming">{l.confirming}</p>
                </>
            ) : (
                <>
                    <span className="w-14 h-14 border border-gold flex items-center justify-center mx-auto mb-8">
                        {isPaid ? <Check size={22} className="text-golddark" /> : <X size={22} className="text-ash" />}
                    </span>
                    <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-charcoal mb-6" data-testid="payment-result-title">
                        {isPaid ? l.paidTitle : variant === "cancel" ? l.cancelTitle : l.failTitle}
                    </h1>
                    <p className="text-sm text-smoke leading-relaxed max-w-md mx-auto mb-12" data-testid="payment-result-body">
                        {isPaid ? l.paidBody : l.failBody}
                    </p>
                    {isPaid && receipt && <Receipt r={receipt} l={l} />}
                    <Link
                        to="/"
                        data-testid="payment-back-button"
                        className="inline-block border border-gold text-golddark px-10 py-4 text-xs tracking-[0.25em] uppercase hover:bg-gold hover:text-ivory transition-colors duration-300"
                    >
                        {l.back}
                    </Link>
                </>
            )}
        </Shell>
    );
}
